/**
 * API Service Base
 * Shared utilities for making authenticated API requests
 */

import { getToken, clearAuthData, isTokenExpired } from '../utils/tokenManager';

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

// Track if a refresh is in progress to prevent multiple simultaneous refreshes
let refreshPromise: Promise<boolean> | null = null;

/**
 * Create a custom error from API response
 */
export const createApiError = async (response: Response): Promise<ApiError> => {
  let errorData: any;

  try {
    errorData = await response.json();
  } catch {
    errorData = { message: 'An unexpected error occurred' };
  }

  return {
    message: errorData.message || errorData.detail || errorData.title || 'An error occurred',
    errors: errorData.errors,
    statusCode: response.status,
  };
};

/**
 * Attempt to refresh the token if it's about to expire
 * Returns true if token is valid (either still valid or successfully refreshed)
 */
const ensureValidToken = async (): Promise<boolean> => {
  const token = getToken();

  if (!token) {
    return false;
  }

  // Check if token needs refresh (expires within 5 minutes)
  if (!isTokenExpired(token)) {
    return true; // Token is still valid
  }

  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh
  refreshPromise = (async () => {
    try {
      const { refreshToken } = await import('./authService');
      const result = await refreshToken();
      return result !== null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Make authenticated API request
 * Automatically refreshes token if it's about to expire
 */
export const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Proactively refresh token if needed (for active users)
  const tokenValid = await ensureValidToken();

  if (!tokenValid) {
    // Token couldn't be refreshed - user needs to re-authenticate
    clearAuthData();
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  // Get the (possibly refreshed) token
  const token = getToken();

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  // A multipart body carries a generated boundary, and only the browser knows it. Setting
  // Content-Type ourselves would send "multipart/form-data" with no boundary and the server
  // would reject the upload as malformed — so let fetch fill it in.
  if (options.body instanceof FormData) {
    delete (defaultHeaders as Record<string, string>)['Content-Type'];
  }

  // Add authorization header if token exists
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Handle 401 Unauthorized - token invalid (shouldn't happen with proactive refresh)
    if (response.status === 401) {
      clearAuthData();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }

    return response;
  } catch (error) {
    // Network error
    if (error instanceof Error && error.message.includes('Session expired')) {
      throw error;
    }

    const networkError: ApiError = {
      message: 'Network error. Please check your connection and try again.',
      statusCode: 0,
    };
    throw networkError;
  }
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiError;

    // Handle validation errors
    if (apiError.errors) {
      const firstErrorKey = Object.keys(apiError.errors)[0];
      const firstError = apiError.errors[firstErrorKey]?.[0];
      if (firstError) {
        return firstError;
      }
    }

    // Handle status-specific errors
    if (apiError.statusCode === 401) {
      return 'Unauthorized. Please log in again.';
    }

    if (apiError.statusCode === 403) {
      return 'You do not have permission to perform this action.';
    }

    if (apiError.statusCode === 404) {
      return 'Resource not found.';
    }

    if (apiError.statusCode === 409) {
      return apiError.message || 'A conflict occurred.';
    }

    if (apiError.statusCode === 422) {
      return apiError.message || 'Validation failed. Please check your input.';
    }

    if (apiError.statusCode === 400) {
      return apiError.message || 'Invalid request. Please check your information and try again.';
    }

    if (apiError.statusCode === 500) {
      return 'Server error. Please try again later.';
    }

    if (apiError.statusCode === 0) {
      return apiError.message || 'Network error. Please check your connection.';
    }

    // Return API error message
    if (apiError.message) {
      return apiError.message;
    }
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};
